import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Analytics } from '../analytics';
import type { MessageSourceType } from '../analytics/types';
import { type FormFactor, getSearchBarPlaceholder } from '../config/constants';
import { getDeploymentTarget, isChromeIndicatorHidden } from '../config/deployment';
import { getDynamicQuestions } from '../config/SiteConfigManager';
import { useConfig } from '../config/unified/ConfigProvider';
import { useChatLanguage, useChatLocale } from '../context/ChatThemeContext';
import { useVisualViewportBottomOffset } from '../hooks/ui/useVisualViewportBottomOffset';
import { useScrollBasedVisibility } from '../hooks/widget/useScrollBasedVisibility';
import {
    buildDynamicQuestionItems,
    createDynamicQuestionRotationTimer,
    getDynamicQuestionDisplayCount,
} from '../utils/content/dynamicQuestions';
import { logger } from '../utils/core/logger';
import SearchBar from './SearchBar';

interface CollapsedChatViewProps {
    onSendMessage: (messageToSend: string, messageSource?: MessageSourceType) => void;
    isLoading: boolean;
    primaryColor: string;
    siteConfig: { name: string };
    domain: string;
    zIndex?: number;
    bypassScrollDetection?: boolean;
    formFactor?: FormFactor;
    hiddenBySectionSearchBar?: boolean;
    onEngage?: () => void;
}

const CollapsedChatView: React.FC<CollapsedChatViewProps> = ({
    onSendMessage,
    isLoading,
    primaryColor,
    siteConfig,
    domain,
    zIndex = 9999,
    bypassScrollDetection = false,
    formFactor = 'default',
    hiddenBySectionSearchBar = false,
    onEngage,
}) => {
    const isCopilot = formFactor === 'copilot';
    const userLanguage = useChatLanguage();
    const userLocale = useChatLocale();
    const placeholderType = isCopilot ? 'COLLAPSED_COPILOT' : 'COLLAPSED';
    const { resolver } = useConfig();
    const placeholder = getSearchBarPlaceholder(
        placeholderType,
        userLanguage,
        siteConfig.name,
        isCopilot ? undefined : (resolver?.appearance.input_placeholder ?? undefined)
    );
    const shouldShowDisclaimer =
        resolver?.forceDisclaimer === true ||
        resolver?.appearance.disclaimers?.always_show === true;
    const visualViewportBottomOffset = useVisualViewportBottomOffset();

    const {
        shouldShowWidget,
        triggeredByScroll,
        viewportHeight,
        scrollPosition,
        isNearBottom,
        isScrollingUp,
    } = useScrollBasedVisibility({
        minViewportHeight: 700,
        minScrollAmount: 150,
        enabled: !bypassScrollDetection,
        bottomThreshold: 50,
        nonScrollableDelay: 4000,
    });

    const [questionRotationTrigger, setQuestionRotationTrigger] = useState(0);
    const [showDynamicQuestions, setShowDynamicQuestions] = useState(false);
    const [areDynamicQuestionsCollapsed, setAreDynamicQuestionsCollapsed] = useState(false);
    const [hasAnimated, setHasAnimated] = useState(false);
    const animationRef = useRef<HTMLDivElement>(null);
    const shuffledIndicesRef = useRef<number[] | null>(null);
    const hasTrackedScrollRevealRef = useRef(false);
    const isUserTypingRef = useRef(false);

    const shuffleArray = useCallback((array: number[]): number[] => {
        return [...array].sort(() => Math.random() - 0.5);
    }, []);

    useEffect(() => {
        if (!shouldShowWidget) {
            setHasAnimated(false);
            setShowDynamicQuestions(false);
            shuffledIndicesRef.current = null;
            return;
        }

        if (animationRef.current) {
            void animationRef.current.offsetHeight;
        }

        const timer = setTimeout(() => {
            setHasAnimated(true);
        }, 100);

        return () => clearTimeout(timer);
    }, [shouldShowWidget]);

    useEffect(() => {
        if (triggeredByScroll && shouldShowWidget && !hasTrackedScrollRevealRef.current) {
            logger.debug('📊 Widget revealed by scroll, tracking analytics (first time only)');
            Analytics.trackWidgetRevealedByScroll({
                viewport_height: viewportHeight,
                scroll_position: scrollPosition,
                page_can_scroll: true,
            });
            hasTrackedScrollRevealRef.current = true;
        }
    }, [triggeredByScroll, shouldShowWidget, viewportHeight, scrollPosition]);

    useEffect(() => {
        if (!shouldShowWidget) {
            return;
        }

        const cleanup = createDynamicQuestionRotationTimer(
            domain,
            () => {
                setShowDynamicQuestions(true);
                setQuestionRotationTrigger((prev) => {
                    return prev + getDynamicQuestionDisplayCount(domain);
                });
            },
            {
                initialDelaySeconds: 5,
                rotationSeconds: 10,
                shouldContinue: () => !isUserTypingRef.current,
            }
        );

        return cleanup;
    }, [domain, shouldShowWidget]);

    const dynamicQuestions = useMemo(() => {
        if (!showDynamicQuestions) {
            return [];
        }

        const currentUrl = typeof window !== 'undefined' ? window.location.href : undefined;
        const allQuestions = getDynamicQuestions(domain, userLocale, currentUrl);

        if (allQuestions.length === 0) {
            return [];
        }

        if (shuffledIndicesRef.current === null) {
            const indices = Array.from({ length: allQuestions.length }, (_, index) => index);
            shuffledIndicesRef.current = shuffleArray(indices);
        }

        return buildDynamicQuestionItems(allQuestions, getDynamicQuestionDisplayCount(domain), {
            rotationOffset: questionRotationTrigger,
            shuffledIndices: shuffledIndicesRef.current,
        });
    }, [domain, userLocale, questionRotationTrigger, showDynamicQuestions, shuffleArray]);

    const handleDynamicQuestionClick = (question: string) => {
        onEngage?.();
        onSendMessage(question, 'dynamic_question');
    };

    const handleDynamicQuestionsCollapseToggle = () => {
        setAreDynamicQuestionsCollapsed(!areDynamicQuestionsCollapsed);
    };

    const handleUserTypingChange = (isTyping: boolean) => {
        isUserTypingRef.current = isTyping;
    };

    if (!shouldShowWidget) {
        return null;
    }

    const isKeyboardOpen = visualViewportBottomOffset > 0;
    const shouldHideAtBottom = !isKeyboardOpen && isNearBottom && !isScrollingUp;
    const shouldHide = shouldHideAtBottom || hiddenBySectionSearchBar;
    const isChromeExtension = getDeploymentTarget() === 'chrome-extension';
    const showChromeIndicator = isChromeExtension && !isChromeIndicatorHidden();
    const baseBottomPx = isCopilot ? 32 : shouldShowDisclaimer ? 0 : 24;

    const getTransform = () => {
        if (isCopilot) {
            if (shouldHide) return 'translateY(calc(100% + 4rem))';
            return hasAnimated ? 'translateY(0)' : 'translateY(calc(100% + 4rem))';
        }

        if (shouldHide) return 'translateX(-50%) translateY(calc(100% + 4rem))';
        return hasAnimated
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(calc(100% + 4rem))';
    };

    return (
        <div
            ref={animationRef}
            className={`chat-collapsed-view fixed
                flex flex-col items-start p-0 m-0 box-border
                border-none outline-none
                ${
                    isCopilot
                        ? `right-8 bottom-8 w-[440px] max-[480px]:w-[calc(100vw-40px)] max-[480px]:right-5`
                        : `left-1/2 w-[800px] max-[840px]:w-[calc(100vw-40px)] max-[840px]:max-w-[calc(100vw-40px)] ${shouldShowDisclaimer ? 'bottom-0' : 'bottom-6'}`
                }`}
            style={{
                zIndex,
                bottom: `${baseBottomPx + visualViewportBottomOffset}px`,
                transform: getTransform(),
                opacity: shouldHide ? 0 : hasAnimated ? 1 : 0,
                transition:
                    'transform 500ms ease-out, opacity 500ms ease-out, width 300ms ease-in-out, max-width 300ms ease-in-out',
                pointerEvents: 'auto',
                ...(showChromeIndicator && {
                    boxShadow: '0 0 0 3px #dc2626, 0 0 12px rgba(220, 38, 38, 0.4)',
                    borderRadius: '24px',
                }),
            }}
            data-ph-capture-attribute-view-type="collapsed-chat"
            data-ph-capture-attribute-form-factor={formFactor}
            data-ph-capture-attribute-site={siteConfig.name}
        >
            <SearchBar
                onSendMessage={onSendMessage}
                isLoading={isLoading}
                primaryColor={primaryColor}
                placeholder={placeholder}
                showPlaceholder={true}
                isExpanded={false}
                siteConfig={siteConfig}
                domain={domain}
                dynamicQuestions={dynamicQuestions}
                showDynamicQuestions={true}
                dynamicQuestionAnimationKey={questionRotationTrigger}
                areDynamicQuestionsCollapsed={areDynamicQuestionsCollapsed}
                onDynamicQuestionsCollapseToggle={handleDynamicQuestionsCollapseToggle}
                onDynamicQuestionClick={handleDynamicQuestionClick}
                showShimmer={true}
                onUserTypingChange={handleUserTypingChange}
                onEngage={onEngage}
                showDisclaimer={shouldShowDisclaimer}
                compactDisclaimer={isCopilot}
            />
        </div>
    );
};

export default CollapsedChatView;
